import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Building2,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  CreditCard,
  Loader2,
  ArrowLeft,
  Landmark,
  Wifi,
  WifiOff,
  DollarSign,
  FileText,
  Check,
  X,
  ChevronRight
} from 'lucide-react';
import PlaidLinkButton from '../components/PlaidLink';
import { API } from '../App';

const BankReconciliation = () => {
  // State
  const [connections, setConnections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unmatched: 0, matched: 0, reconciled: 0 });
  const [totals, setTotals] = useState({ unmatched: 0, matched: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [activeTab, setActiveTab] = useState('connections'); // connections, transactions
  const [transactionFilter, setTransactionFilter] = useState('unmatched');
  const [plaidStatus, setPlaidStatus] = useState({ configured: false, message: '' });

  // Fetch data on mount
  useEffect(() => {
    fetchPlaidStatus();
    fetchConnections();
    fetchTransactions();
  }, [transactionFilter]);

  const fetchPlaidStatus = async () => {
    try {
      const response = await API.get('/plaid/status');
      setPlaidStatus(response.data);
    } catch (error) {
      console.error('Error fetching Plaid status:', error);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await API.get('/plaid/connections');
      setConnections(response.data.connections);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await API.get(`/plaid/transactions?status=${transactionFilter}`);
      setTransactions(response.data.transactions);
      setCounts(response.data.counts);
      setTotals(response.data.totals || { unmatched: 0, matched: 0 });
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connectionId) => {
    setSyncing(prev => ({ ...prev, [connectionId]: true }));
    
    try {
      const response = await API.post(`/plaid/connections/${connectionId}/sync`);
      toast.success(response.data.message);
      fetchConnections();
      fetchTransactions();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync transactions');
    } finally {
      setSyncing(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  const handleDisconnect = async (connectionId, institutionName) => {
    if (!window.confirm(`Disconnect from ${institutionName}? This won't delete existing transactions.`)) {
      return;
    }
    
    try {
      await API.delete(`/plaid/connections/${connectionId}`);
      toast.success('Bank account disconnected');
      fetchConnections();
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect bank');
    }
  };

  const handleReconcile = async (transactionId) => {
    try {
      await API.post(`/plaid/transactions/${transactionId}/reconcile`);
      toast.success('Transaction reconciled');
      fetchTransactions();
    } catch (error) {
      console.error('Error reconciling:', error);
      toast.error(error.response?.data?.detail || 'Failed to reconcile');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount || 0));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="min-h-screen text-white p-6" style={{ backgroundColor: '#020617' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div 
            className="p-2.5 rounded-xl"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)' }}
          >
            <Landmark className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white tracking-wide">BANK RECONCILIATION</h1>
            <p className="text-slate-400 text-sm">Connect accounts & match transactions</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Plaid Status Indicator */}
          <div 
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
              plaidStatus.configured 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
            }`}
          >
            {plaidStatus.configured ? (
              <>
                <Wifi className="h-4 w-4" />
                Plaid Connected
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4" />
                Plaid Not Configured
              </>
            )}
          </div>
          
          <PlaidLinkButton 
            onSuccess={() => {
              fetchConnections();
              fetchTransactions();
            }} 
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div 
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#0f172a', border: '1px solid rgba(251, 191, 36, 0.3)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Unmatched</span>
            <AlertCircle className="h-4 w-4 text-amber-400" />
          </div>
          <p className="text-2xl font-bold text-amber-400 font-mono">{counts.unmatched}</p>
          <p className="text-slate-500 text-xs mt-1">{formatCurrency(totals.unmatched)} total</p>
        </div>
        
        <div 
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#0f172a', border: '1px solid rgba(59, 130, 246, 0.3)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Matched</span>
            <FileText className="h-4 w-4 text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-blue-400 font-mono">{counts.matched}</p>
          <p className="text-slate-500 text-xs mt-1">{formatCurrency(totals.matched)} total</p>
        </div>
        
        <div 
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#0f172a', border: '1px solid rgba(16, 185, 129, 0.3)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Reconciled</span>
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          </div>
          <p className="text-2xl font-bold text-emerald-400 font-mono">{counts.reconciled}</p>
          <p className="text-slate-500 text-xs mt-1">Verified & locked</p>
        </div>
        
        <div 
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#0f172a', border: '1px solid rgba(100, 116, 139, 0.3)' }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-xs uppercase tracking-wider">Connections</span>
            <Building2 className="h-4 w-4 text-slate-400" />
          </div>
          <p className="text-2xl font-bold text-white font-mono">{connections.length}</p>
          <p className="text-slate-500 text-xs mt-1">Bank accounts</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('connections')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'connections'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white'
          }`}
        >
          <Building2 className="h-4 w-4 inline-block mr-2" />
          Bank Accounts ({connections.length})
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'transactions'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
              : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white'
          }`}
        >
          <DollarSign className="h-4 w-4 inline-block mr-2" />
          Transactions ({counts.total})
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
        </div>
      )}

      {/* CONNECTIONS TAB */}
      {!loading && activeTab === 'connections' && (
        <div className="space-y-4">
          {/* No Connections */}
          {connections.length === 0 && (
            <div 
              className="text-center p-12 rounded-xl"
              style={{ backgroundColor: '#0f172a', border: '1px solid rgba(100, 116, 139, 0.3)' }}
            >
              <Building2 className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">No Bank Accounts Connected</h2>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                Connect your bank account to automatically import transactions for reconciliation.
                {!plaidStatus.configured && (
                  <span className="block mt-2 text-amber-400 text-sm">
                    Note: Plaid API credentials need to be configured first.
                  </span>
                )}
              </p>
              <PlaidLinkButton onSuccess={fetchConnections} />
            </div>
          )}

          {/* Connections List */}
          {connections.map((connection) => (
            <div
              key={connection.connection_id}
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: '#0f172a', border: '1px solid rgba(100, 116, 139, 0.3)' }}
            >
              {/* Connection Header */}
              <div 
                className="p-5 flex items-center justify-between"
                style={{ borderBottom: '1px solid rgba(100, 116, 139, 0.2)' }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)' }}
                  >
                    <Building2 className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {connection.institution_name}
                    </h3>
                    <p className="text-slate-400 text-sm">
                      Connected {formatDate(connection.created_at)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSync(connection.connection_id)}
                    disabled={syncing[connection.connection_id]}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white hover:bg-slate-700 disabled:opacity-50 transition-all"
                  >
                    {syncing[connection.connection_id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Sync
                  </button>
                  <button
                    onClick={() => handleDisconnect(connection.connection_id, connection.institution_name)}
                    className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Accounts */}
              <div className="p-5">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-3">
                  Linked Accounts
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {connection.accounts?.map((account) => (
                    <div
                      key={account.account_id}
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(100, 116, 139, 0.2)' }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <CreditCard className="h-5 w-5 text-slate-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {account.name}
                          </p>
                          <p className="text-slate-500 text-xs">
                            •••• {account.mask}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 capitalize">
                          {account.subtype}
                        </span>
                        <span className="text-emerald-400 font-semibold font-mono">
                          {formatCurrency(account.current_balance)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Last Synced */}
                <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="h-3 w-3" />
                  Last synced: {formatDateTime(connection.last_synced_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TRANSACTIONS TAB */}
      {!loading && activeTab === 'transactions' && (
        <div>
          {/* Transaction Filters */}
          <div className="flex gap-2 mb-4">
            {['unmatched', 'matched', 'reconciled', 'all'].map((filter) => (
              <button
                key={filter}
                onClick={() => setTransactionFilter(filter)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  transactionFilter === filter
                    ? filter === 'unmatched' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                    : filter === 'matched' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                    : filter === 'reconciled' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                    : 'bg-slate-500/20 text-white border border-slate-500/40'
                    : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
                {filter === 'unmatched' && counts.unmatched > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/30 text-xs">
                    {counts.unmatched}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Transactions List */}
          <div 
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: '#0f172a', border: '1px solid rgba(100, 116, 139, 0.3)' }}
          >
            {/* Table Header */}
            <div 
              className="grid grid-cols-12 gap-4 px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400"
              style={{ backgroundColor: 'rgba(15, 23, 42, 0.8)', borderBottom: '1px solid rgba(100, 116, 139, 0.2)' }}
            >
              <div className="col-span-2">Date</div>
              <div className="col-span-4">Description</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-2 text-right">Status</div>
            </div>

            {/* No Transactions */}
            {transactions.length === 0 && (
              <div className="text-center p-8 text-slate-500">
                <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No {transactionFilter !== 'all' ? transactionFilter : ''} transactions found</p>
                {connections.length > 0 && transactionFilter === 'unmatched' && (
                  <p className="text-sm mt-2">Try syncing your bank accounts to import transactions</p>
                )}
              </div>
            )}

            {/* Transaction Rows */}
            {transactions.map((txn) => (
              <div 
                key={txn.transaction_id}
                className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-slate-800/30 transition-all"
                style={{ borderBottom: '1px solid rgba(100, 116, 139, 0.1)' }}
              >
                <div className="col-span-2 text-slate-300 font-mono text-sm">
                  {txn.date}
                </div>
                <div className="col-span-4">
                  <p className="text-white font-medium truncate">{txn.merchant_name || txn.description}</p>
                  {txn.merchant_name && txn.description !== txn.merchant_name && (
                    <p className="text-slate-500 text-xs truncate">{txn.description}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <span className="px-2 py-1 rounded text-xs bg-slate-700 text-slate-300">
                    {txn.category?.[0] || 'Uncategorized'}
                  </span>
                </div>
                <div className={`col-span-2 text-right font-mono font-bold ${
                  txn.amount > 0 ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  {txn.amount > 0 ? '-' : '+'}${Math.abs(txn.amount).toFixed(2)}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  {txn.is_reconciled ? (
                    <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      <CheckCircle2 className="h-3 w-3" />
                      Reconciled
                    </span>
                  ) : txn.match_status === 'matched' ? (
                    <button
                      onClick={() => handleReconcile(txn.transaction_id)}
                      className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-all"
                    >
                      <Check className="h-3 w-3" />
                      Confirm
                    </button>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      <AlertCircle className="h-3 w-3" />
                      Review
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Note */}
      <div 
        className="mt-8 p-4 rounded-xl"
        style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', border: '1px solid rgba(100, 116, 139, 0.2)' }}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5" />
          <div>
            <p className="text-white font-medium">Secure Connection</p>
            <p className="text-slate-400 text-sm">
              Bank connections are powered by Plaid, the same security infrastructure used by 
              Venmo, Robinhood, and thousands of other financial apps. Your bank credentials 
              are never stored on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankReconciliation;
